import * as React from 'react';
import useSWR from "swr";
import Cosmic from "cosmicjs";
import { useSearchParams, Link } from "react-router-dom";

import "css/blog.css";
import "css/tokens.css";
import {
  buildPageTitle,
  buildPageDescription,
  buildCanonicalUrl,
  buildKeywords,
  DEFAULT_OG_IMAGE,
} from 'seo/seoConfig';
import StructuredData from 'seo/StructuredData';
import { AI_META_TAGS, AI_LINK_TAGS, buildAiCitationMeta } from 'seo/aiMeta';
import { getThDate } from 'components/lotto/utility/Utility.js';

import SocialShare from "components/lotto/default/SocialShare";
import DirectAdsCarousel from "components/lotto/default/DirectAdsCarousel";
import DirectAdsComponent from "components/lotto/default/DirectAdsComponent";
import AffiliateCta from 'affiliate/AffiliateCta';
import { AFFILIATE_MESSAGES } from 'affiliate/affiliateConfig';
import ContactUs from "components/lotto/default/ContactUs";

import { Helmet } from "react-helmet";

const api = Cosmic();

const bucket = api.bucket({
  slug: "293f2770-8a68-11ed-b039-3f473b7cfbb9",
  read_key: "OuUQ2vnL5dNA6QtpbLFDvdyPFZ3L4MZcTFkHdjIKAakYltWFlg",
});

const ArticleDetail = () => {
  const [searchParams] = useSearchParams();
  const slug = searchParams?.get('slug');

  const fetchPosts = async () => {
    const data = await bucket.objects
      .find({ type: "posts", slug })
      .props('slug,title,thumbnail,content,metadata,published_at');
    return data;
  };

  const { data, error } = useSWR(slug ? `fetch-post-${slug}` : null, fetchPosts);

  if (!slug) {
    return <main className="status-message status-error">ไม่พบบทความ</main>;
  }
  if (!data && !error) {
    return <main className="status-message status-loading" role="status">กำลังโหลดบทความ...</main>;
  }
  if (error || !data?.objects?.[0]) {
    return <main className="status-message status-error" role="alert">ไม่พบบทความนี้</main>;
  }

  const post = data.objects[0];
  const articleTeaser = post.metadata?.teaser || post.title;
  const pageTitle = buildPageTitle('article', slug, post.title);
  const pageDescription = buildPageDescription('article', slug, articleTeaser);
  const canonicalUrl = buildCanonicalUrl('article', slug);
  const ogImage = post.thumbnail
    ? `${post.thumbnail}?w=1200&auto=format`
    : DEFAULT_OG_IMAGE;

  return (
    <>
      <Helmet>
        <html lang="th" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={buildKeywords('article', slug)} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="th-th" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:locale" content="th_TH" />
        <meta property="article:published_time" content={post.published_at} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
        {AI_META_TAGS.map((tag) => (
          <meta key={tag.name} name={tag.name} content={tag.content} />
        ))}
        {AI_LINK_TAGS.map((tag) => (
          <link key={`${tag.rel}-${tag.href}`} rel={tag.rel} href={tag.href} {...(tag.type ? { type: tag.type } : {})} {...(tag.title ? { title: tag.title } : {})} />
        ))}
        {buildAiCitationMeta(canonicalUrl).map((tag) =>
          tag.name ? (
            <meta key={tag.name} name={tag.name} content={tag.content} />
          ) : (
            <meta key={tag.property} property={tag.property} content={tag.content} />
          )
        )}
      </Helmet>

      <StructuredData
        mode="article"
        keyword={slug}
        extras={{
          articleTitle: post.title,
          articleTeaser,
          articleImage: ogImage,
          publishedAt: post.published_at,
          authorName: post.metadata?.author?.title,
        }}
      />

      <div className="hotto-app">
        {post.thumbnail && (
          <div
            className="blog-post-hero"
            style={{ backgroundImage: `url(${ogImage})` }}
            role="img"
            aria-label={post.title}
          />
        )}
        <main className="container" id="main-content">
          <article className="card-padding" itemScope itemType="https://schema.org/Article">
            <header>
              <h1 className="blog__title" itemProp="headline">
                <Link to={`/article?slug=${post.slug}`}>{post.title}</Link>
              </h1>
              <div className="blog__author">
                <div
                  className="blog__author-image"
                  style={{
                    backgroundImage: `url(${post.metadata?.author?.metadata?.image?.imgix_url}?w=100)`,
                  }}
                  role="img"
                  aria-label={post.metadata?.author?.title}
                />
                <p className="blog__author-title">
                  โดย <span itemProp="author">{post.metadata?.author?.title}</span>
                  {' '}วันที่{' '}
                  <time itemProp="datePublished" dateTime={post.published_at}>
                    {getThDate(post.published_at)}
                  </time>
                </p>
              </div>
            </header>

            <div className="c">
              <SocialShare />
            </div>

            <div
              className="blog__teaser droid"
              itemProp="articleBody"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            <AffiliateCta variant="strip" message={AFFILIATE_MESSAGES.general} />

            <div className="c">
              <SocialShare />
            </div>
          </article>
        </main>

        <div className="c95in100">
          <DirectAdsCarousel />
          <DirectAdsComponent adsType="lazada_bonus_deeplink" />
          <hr />
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default ArticleDetail;
